<template>
  <div class="flex flex-col gap-2">
    <label v-if="label" :for="inputId" class="font-medium text-text-primary text-[14px]">
      {{ label }}
      <span v-if="required" class="text-red">*</span>
    </label>

    <input
      :id="inputId"
      :type="type"
      :value="modelValue"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      :maxlength="maxlength"
      :class="inputClass"
      :aria-invalid="hasError"
      :aria-describedby="hasError ? `${inputId}-error` : undefined"
      style="font-size: 16px"
      @input="handleInput"
      @blur="handleBlur"
    />

    <span v-if="error" :id="`${inputId}-error`" class="text-red text-[14px]" role="alert">
      {{ error }}
    </span>

    <span v-if="hint && !error" class="text-text-muted text-[14px]">
      {{ hint }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  modelValue: string | number
  label?: string
  type?: 'text' | 'email' | 'tel' | 'password' | 'number' | 'date'
  placeholder?: string
  disabled?: boolean
  readonly?: boolean
  required?: boolean
  error?: string | undefined
  hint?: string
  id?: string
  maxlength?: number
  variant?: 'default' | 'surface'
}

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  disabled: false,
  readonly: false,
  required: false,
  variant: 'default',
})

const emit = defineEmits<{
  'update:modelValue': [value: string | number]
  blur: []
}>()

const inputId = computed(() => props.id || `input-${Math.random().toString(36).substr(2, 9)}`)

const hasError = computed(() => !!props.error)

const inputClass = computed(() => {
  const baseClasses =
    'w-full px-3 py-3 text-[14px] font-sans transition-colors duration-200 bg-white text-text-primary focus:outline-none placeholder:text-text-muted'

  const variantClasses =
    props.variant === 'surface'
      ? 'border border-black/5 rounded-2xl shadow-[0_10px_30px_rgba(0,0,0,0.10)] focus:border-primary'
      : 'border-2 border-border rounded-xl focus:border-primary'

  const errorClasses = hasError.value ? 'border-red focus:border-red' : ''

  const disabledClasses = props.disabled ? 'bg-border-light cursor-not-allowed' : ''

  return [baseClasses, variantClasses, errorClasses, disabledClasses].filter(Boolean).join(' ')
})

const handleInput = (event: Event): void => {
  const target = event.target as HTMLInputElement
  const value = props.type === 'number' ? Number(target.value) : target.value
  emit('update:modelValue', value)
}

const handleBlur = (): void => {
  emit('blur')
}
</script>
